// WHIT-485 round 2: the destination and theme pages the incumbent site had
// (/torokorszag, /last-minute-utazasok, ...), each a package search filled in
// in advance. The English address is /holidays/<slug>/, the Hungarian one is
// the incumbent's, so WHIT-486 can redirect the old links one to one.
// A page holds no dates of its own: they are worked out from the day the page
// is opened, so a page never offers a departure in the past.
// Pure (no DOM, no React) so `node --test web/utils/packageLandingPages.test.js`
// can load it.

const { DEFAULTS } = require('./packageSearch');

// Days from today to the first departure, and how many days of departures.
const LEAD_DAYS = 7;
const WINDOW_DAYS = 30;
const LAST_MINUTE_DAYS = 14;
// Oman and Zanzibar are sold for the autumn and winter: October to March.
const SEASON_FIRST_MONTH = 10;
const SEASON_LAST_MONTH = 3;

const country = id => ({ level: 'country', id });

/**
 * @typedef {{
 *   slug: string,
 *   huPath: string,
 *   kind: 'destination' | 'theme',
 *   dates?: 'lastMinute' | 'winterSeason',
 *   search: Partial<import('./packageSearch').PackageSearch>,
 * }} LandingPage
 */

/** @type {LandingPage[]} */
const LANDING_PAGES = [
  {
    slug: 'turkey',
    huPath: '/torokorszag/',
    kind: 'destination',
    search: { destination: country('TR') },
  },
  {
    slug: 'egypt',
    huPath: '/egyiptom/',
    kind: 'destination',
    search: { destination: country('EG') },
  },
  {
    slug: 'greece',
    huPath: '/gorogorszag/',
    kind: 'destination',
    search: { destination: country('GR') },
  },
  {
    slug: 'spain',
    huPath: '/spanyolorszag/',
    kind: 'destination',
    search: { destination: country('ES') },
  },
  {
    slug: 'cyprus',
    huPath: '/ciprus/',
    kind: 'destination',
    search: { destination: country('CY') },
  },
  {
    slug: 'portugal',
    huPath: '/portugalia/',
    kind: 'destination',
    search: { destination: country('PT') },
  },
  {
    slug: 'oman',
    huPath: '/oman-osszel-telen/',
    kind: 'destination',
    dates: 'winterSeason',
    search: { destination: country('OM') },
  },
  {
    // Zanzibar is a region of Tanzania; region ids are the provider's and
    // change, so the page searches the country.
    slug: 'zanzibar',
    huPath: '/zanzibar-osszel-telen/',
    kind: 'destination',
    dates: 'winterSeason',
    search: { destination: country('TZ') },
  },
  {
    slug: 'last-minute',
    huPath: '/last-minute-utazasok/',
    kind: 'theme',
    dates: 'lastMinute',
    search: {},
  },
  {
    // Two adults and one child; the visitor sets the real ages in the form.
    slug: 'family-holidays',
    huPath: '/csaladi-nyaralas/',
    kind: 'theme',
    search: { childAges: [6] },
  },
  {
    slug: 'beach-hotels',
    huPath: '/tengerparti-szallodak/',
    kind: 'theme',
    search: { category: 5, amenities: ['sandy_beach'] },
  },
  {
    // The package search has no round-trip product; short trips are the
    // city breaks of this page.
    slug: 'city-breaks',
    huPath: '/korutazas-varoslatogatas/',
    kind: 'theme',
    search: { minDays: 2, maxDays: 5 },
  },
];

const landingPage = slug =>
  LANDING_PAGES.find(page => page.slug === slug) || null;

const landingPath = slug => `/holidays/${slug}/`;

// The urlTranslations entries for the Hungarian addresses.
const landingRewrites = () =>
  Object.fromEntries(
    LANDING_PAGES.map(page => [
      page.huPath,
      { locale: 'hu', rewriteUrl: landingPath(page.slug) },
    ]),
  );

// Calendar arithmetic on YYYY-MM-DD, in UTC so the server's time zone does
// not move a date.
const addDays = (iso, days) => {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const isoDate = (year, month, day) =>
  new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);

const inWinterSeason = iso => {
  const month = Number(iso.slice(5, 7));
  return month >= SEASON_FIRST_MONTH || month <= SEASON_LAST_MONTH;
};

// The season's last day for a date inside it: 31 March of the same or the
// next year.
const seasonEnd = iso => {
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7));
  return isoDate(month >= SEASON_FIRST_MONTH ? year + 1 : year, 3, 31);
};

const departureWindow = (page, today) => {
  if (page.dates === 'lastMinute')
    return { from: addDays(today, 1), to: addDays(today, LAST_MINUTE_DAYS) };
  let from = addDays(today, LEAD_DAYS);
  if (page.dates !== 'winterSeason')
    return { from, to: addDays(from, WINDOW_DAYS) };
  if (!inWinterSeason(from))
    from = isoDate(Number(from.slice(0, 4)), SEASON_FIRST_MONTH, 1);
  const to = addDays(from, WINDOW_DAYS);
  const end = seasonEnd(from);
  return { from, to: to > end ? end : to };
};

/**
 * The page's search on the given day (YYYY-MM-DD).
 * @param {LandingPage} page
 * @param {string} today
 */
const landingSearch = (page, today) => {
  const { from, to } = departureWindow(page, today);
  // A copy, so a change to one visit's search (an amenity added in the form)
  // cannot reach the page's definition or the defaults.
  return structuredClone({
    ...DEFAULTS,
    ...page.search,
    dateFrom: from,
    dateTo: to,
  });
};

// Round 3: the home page's top offers (the incumbent's "Kiemelt
// ajánlataink"), one tab per country, each its cheapest hotels over the same
// dates as a destination page. `name` is the tab's translation key.
const TOP_OFFER_COUNTRIES = [
  { id: 'ES', name: 'Spain' },
  { id: 'PT', name: 'Portugal' },
  { id: 'IT', name: 'Italy' },
  { id: 'EG', name: 'Egypt' },
];

const topOfferSearch = (countryId, today) =>
  landingSearch({ search: { destination: country(countryId) } }, today);

module.exports = {
  TOP_OFFER_COUNTRIES,
  topOfferSearch,
  LANDING_PAGES,
  landingPage,
  landingPath,
  landingRewrites,
  landingSearch,
};
